/**
 * Keeps an open sheet in step with the server.
 *
 * Autosave, debounced, with the row version carried through so two tabs cannot
 * silently overwrite each other (CLAUDE.md section 10.2). A conflict is surfaced
 * rather than resolved: the engineer is told their copy is behind, because merging
 * two divergent calculations without asking is exactly the kind of helpfulness that
 * loses somebody's afternoon.
 *
 * Every edit is written to the local draft *before* the network is attempted, so a
 * dropped connection costs nothing (section 14.1).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { ApiError } from '@/api/client'
import { clearDraft, updateSheet, writeDraft } from './api'
import type { SheetDoc } from './model/types'

const SAVE_DEBOUNCE_MS = 1200

export type SyncStatus = 'idle' | 'unsaved' | 'saving' | 'saved' | 'conflict' | 'error'

export interface SheetSync {
  readonly status: SyncStatus
  readonly rowVersion: number
  /**
   * Forces a save now and resolves when it has landed.
   *
   * Awaitable on purpose: publishing copies the document *the server holds*, so
   * issuing a version while a debounced save is still in flight would freeze the
   * previous content and hand a colleague the wrong calculation.
   */
  saveNow(): Promise<void>
}

export function useSheetSync(
  sheetId: number | null,
  doc: SheetDoc,
  initialRowVersion: number,
  options: { readonly enabled?: boolean } = {},
): SheetSync {
  const enabled = options.enabled ?? true
  const [status, setStatus] = useState<SyncStatus>('idle')
  const [rowVersion, setRowVersion] = useState(initialRowVersion)

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const latest = useRef(doc)
  const inFlight = useRef(false)
  // The document as the server last confirmed it, so an unchanged doc is not saved
  // repeatedly just because React re-rendered.
  const persisted = useRef(JSON.stringify(doc))

  const push = useCallback(async () => {
    if (sheetId === null || inFlight.current) return
    const snapshot = JSON.stringify(latest.current)
    if (snapshot === persisted.current) {
      setStatus('saved')
      return
    }

    inFlight.current = true
    setStatus('saving')
    try {
      const saved = await updateSheet(sheetId, {
        row_version: rowVersion,
        title: latest.current.title,
        document: latest.current,
      })
      persisted.current = snapshot
      setRowVersion(saved.row_version)
      setStatus('saved')
      // The draft has served its purpose; keeping it would resurrect old work on the
      // next open.
      clearDraft(sheetId)
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        // Someone else saved first. The local draft stays on disk so nothing is lost
        // while the engineer decides what to do.
        setStatus('conflict')
      } else {
        setStatus('error')
      }
    } finally {
      inFlight.current = false
    }
  }, [rowVersion, sheetId])

  useEffect(() => {
    // Written in an effect, not during render: `push` runs from a timeout that
    // always fires after this, so it still sees the newest document.
    latest.current = doc
  }, [doc])

  useEffect(() => {
    if (!enabled || sheetId === null) return
    if (JSON.stringify(doc) === persisted.current) return

    // Local first, network second: the draft must survive a save that never lands.
    writeDraft(sheetId, doc, rowVersion)
    setStatus((current) => (current === 'conflict' ? current : 'unsaved'))

    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => void push(), SAVE_DEBOUNCE_MS)

    return () => {
      if (timer.current) clearTimeout(timer.current)
    }
  }, [doc, enabled, push, rowVersion, sheetId])

  const saveNow = useCallback(async () => {
    if (timer.current) clearTimeout(timer.current)
    await push()
  }, [push])

  // Memoised: a fresh object every render makes every `useEffect([sync])` in a
  // consumer re-run on every render, and a cleanup that flushes becomes an infinite
  // save loop. That is not hypothetical -- it is what the e2e run caught.
  return useMemo(() => ({ status, rowVersion, saveNow }), [rowVersion, saveNow, status])
}
